import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import ModalDialog, { ModalBody, ModalTitle, ModalHeader } from '@atlaskit/modal-dialog';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';

import type { Template } from '@confluence/template-list/entry-points/TemplateListQuery.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

import { useDiscardChanges } from './useDiscardChanges.ts';
import {
	DialogActionsContainer,
	DiscardDraftContainer,
	DiscardDraftHeader,
} from './DiscardChangesModalStyled.tsx';

const i18nBase = defineMessages({
	discardChangesModalDialogTitle: {
		id: 'template-panel.always-discard-modal.title',
		defaultMessage: 'Replace existing content with template?',
		description:
			'The title of the modal dialog that opens when a user selects another template to use in the editor, but already has user content',
	},
	discardChangesModalDialogTitleWarning: {
		id: 'template-panel.always-discard-modal.title.warning',
		defaultMessage: 'Warning',
		description:
			'The label of the warning icon of the modal dialog that opens when a user selects a template to use and already has content in the editor',
	},
	discardChangesCancelButtonText: {
		id: 'template-panel.always-discard-modal.button.cancel',
		defaultMessage: 'Cancel',
		description:
			'Button text to close the modal dialog that opens when a user selects a template to use and already has content in the editor',
	},
	discardChangesCreateButtonText: {
		id: 'template-panel.always-discard-modal.button.create',
		defaultMessage: 'Replace content',
		description:
			'Button text to move to the next step of using a template in the modal dialog that opens when a user selects a template to use and already has content in the editor',
	},
	discardChangesModalDialogBodyText: {
		id: 'template-panel.always-discard-modal.body',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"The content you've created so far will be lost. To use a template without losing your work, create a new page and select a template before adding any content.",
		description:
			'The text inside the body of the modal dialog that opens when a user selects a template to use and already has content in the editor',
	},
});

const i18nLiveDocs = defineMessages({
	discardChangesModalDialogBodyText: {
		id: 'template-panel.always-discard-modal.body.livedocs',
		defaultMessage:
			'The content you’ve created so far will be lost. To use a template without losing your work, create a new page or live doc and select a template before adding any content.',
		description: 'Updated version of template-panel.always-discard-modal.body',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

export type AlwaysDiscardChangesModalProps = {
	onCreate: (template: Template, replaceContent?: boolean) => void;
	onClose: () => void;
	template: Template;
};

export const AlwaysDiscardChangesModal: React.FC<AlwaysDiscardChangesModalProps> = React.memo(
	({ onClose, onCreate, template }) => {
		const intl = useIntl();
		const { onCreateHandler, onCloseHandler } = useDiscardChanges({
			discardDraftDefault: true,
			onClose,
			onCreate,
			template,
		});

		return (
			<ModalDialog onClose={onCloseHandler} width={480} testId="always-discard-changes-modal">
				<ModalHeader hasCloseButton>
					<ModalTitle>
						<DiscardDraftHeader>
							<StatusWarningIcon
								spacing="spacious"
								color={token('color.icon.warning')}
								label={intl.formatMessage(i18n.discardChangesModalDialogTitleWarning)}
							/>
							<FormattedMessage {...i18n.discardChangesModalDialogTitle} />
						</DiscardDraftHeader>
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<FormattedMessage {...i18n.discardChangesModalDialogBodyText} />
					<DiscardDraftContainer>
						<DialogActionsContainer>
							<ButtonGroup>
								<Button
									testId="always-discard-changes-modal-button-cancel"
									onClick={onCloseHandler}
									appearance="subtle"
								>
									<FormattedMessage {...i18n.discardChangesCancelButtonText} />
								</Button>
								<Button
									testId="always-discard-changes-modal-button-create-primary"
									onClick={onCreateHandler}
									appearance="warning"
								>
									<FormattedMessage {...i18n.discardChangesCreateButtonText} />
								</Button>
							</ButtonGroup>
						</DialogActionsContainer>
					</DiscardDraftContainer>
				</ModalBody>
			</ModalDialog>
		);
	},
);
